import { AuthError, EventMessage, EventType } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { app, getQueryString, routing, useMapDispatch, useMapState } from "@comact/crc";
import _ from "lodash";
import React from "react";
import { IUser, IUserMSAL, UserModule } from "../users";
import { getActiveAccount, getB2cPolicies, getMsalInstance, recoverSignIn, setActiveAccount } from "./msalAuthoConfig";
import { isDeadGrantError } from "./signInRecovery";

const FROM_EXTERNAL_LOGIN_TAB = "FROM_EXTERNAL_LOGIN_TAB";

const CODES = {
    forgotPassword: "AADB2C90118",
    cancelEditProfile: "AADB2C90091",
} as const;

const useCheckUser = (isIframe: boolean = false) => {
    const prevUser = React.useRef<IUser>(null);

    const currentUser = useMapState((state) => UserModule.selectors.getCurrentUser(state));

    const setUserMSAL = useMapDispatch((dispatch) => (
        (userMSAL: IUserMSAL) => {
            dispatch(UserModule.actions.setUserFromMSAL(userMSAL));
            if (!_.isEqual(currentUser, prevUser.current)) {
                if (app.i18next.isInitialized && currentUser?.locale) app.changeLanguage(currentUser.locale);
                prevUser.current = currentUser;
            }
        }
    ), [currentUser]);

    const checkUser = React.useMemo(() => _.throttle(async () => {
        // Serverless user
        if (app.serverLess) {
            setUserMSAL(await import("../users/mocks").then((m) => m.mockUserMSAL));
            return;
        }

        const msalInstance = getMsalInstance();
        const activeAccount = getActiveAccount();

        setActiveAccount(activeAccount);
        setUserMSAL(activeAccount);
        if (!activeAccount) {
            if (!isIframe) msalInstance.loginRedirect({ scopes: [getB2cPolicies().readScope], redirectUri: routing.projectPath });
        } else {
            // Try to close (if this window has been opened by "MsalConditionalProviderExternalLoginTab")
            if (sessionStorage.getItem(FROM_EXTERNAL_LOGIN_TAB) == "true") {
                window.close();
            }
        }
        setTimeout(checkUser, 7 * 1000); // call it ourself in case we don't have any events when the use token expired
    }, 5 * 1000), [setUserMSAL, isIframe]);

    return checkUser;
};

const MsalConditionalProvider = React.memo<{ children: React.ReactNode; }>((({ children }) => {
    if (app.serverLess) return <>{children}</>;
    const externalLogin = getQueryString("externalLogin") == "true";

    const msalInstance = getMsalInstance();
    if (!msalInstance) { // Not ready yet
        return null;
    } else if (externalLogin) { // Open a tab to login and then close it
        return <MsalConditionalProviderExternalLoginTab />;
    } else if (window !== window.parent) { // The page is in an iframe
        return <MsalConditionalProviderIframe children={children} />;
    } else { // The page is not in an iframe
        return <MsalConditionalProviderRedirect children={children} />;
    }
}));

// Tab : Go to login page immediately
const MsalConditionalProviderExternalLoginTab = (() => {
    sessionStorage.setItem(FROM_EXTERNAL_LOGIN_TAB, "true"); // Keep in session to be able to automatically close the page later...
    const msalInstance = getMsalInstance();
    React.useEffect(() => {
        msalInstance.handleRedirectPromise()
            .then((result) => {
                if (result?.account) setActiveAccount(result.account as IUserMSAL);
            })
            .finally(() => {
                msalInstance.loginRedirect({ scopes: [getB2cPolicies().readScope], redirectUri: routing.projectPath });
            });
    }, [msalInstance]);
    return <MsalProvider instance={msalInstance} />;
});

// Iframe : we don't need to check any error because we should arrive here already login from another tab that handle de login
const MsalConditionalProviderIframe = (({ children }: { children: React.ReactNode; }) => {
    const checkUser = useCheckUser(true);
    const msalInstance = getMsalInstance();
    React.useEffect(() => {
        msalInstance.handleRedirectPromise()
            .then((result) => {
                if (result?.account) setActiveAccount(result.account as IUserMSAL);
            })
            .finally(() => {
                checkUser();
                msalInstance.addEventCallback((event: EventMessage) => {
                    if (_.includes([EventType.HANDLE_REDIRECT_END, EventType.ACQUIRE_TOKEN_FAILURE, EventType.LOGIN_SUCCESS, EventType.LOGOUT_SUCCESS], event.eventType)) {
                        checkUser.flush(); // call it now
                    }
                });
            });
    }, [checkUser, msalInstance]);

    return <MsalProvider instance={msalInstance} children={children} />;
});

// Normal
const MsalConditionalProviderRedirect = (({ children }: { children: React.ReactNode; }) => {
    const checkUser = useCheckUser();
    const msalInstance = getMsalInstance();
    React.useEffect(() => {
        msalInstance.handleRedirectPromise()
            .then((result) => {
                if (result?.account) setActiveAccount(result.account as IUserMSAL);
            })
            .catch(async (e: AuthError) => {
                if (e.errorMessage?.indexOf(CODES.forgotPassword) > -1) { // Error message has this error id to manage the "forgot password" form
                    msalInstance.loginRedirect(getB2cPolicies().authorities.forgotPassword); // forgot password
                } else if (e.errorMessage?.indexOf(CODES.cancelEditProfile) > -1) { // Cancel "edit profile" form
                    // Do nothing, the user should return to the page logged
                } else if (e.errorCode == "no_cached_authority_error") {
                    // This happen sometimes for unknown reasons, reloading the page fix the bug
                    document.location.reload();
                } else if (isDeadGrantError(e)) {
                    // The sign-in response was refused as an expired grant: without this, checkUser would start the same sign-in again, for ever.
                    // Start a clean one instead, once (it leaves the page; when it does not start, the error shows).
                    if (!await recoverSignIn(e, routing.projectPath)) console.error(e);
                } else {
                    console.error(e);
                }
            })
            .finally(() => {
                checkUser();
                msalInstance.addEventCallback((event: EventMessage) => {
                    if (_.includes([EventType.HANDLE_REDIRECT_END, EventType.ACQUIRE_TOKEN_FAILURE, EventType.LOGIN_SUCCESS, EventType.LOGOUT_SUCCESS], event.eventType)) {
                        checkUser.flush(); // call it now
                    }
                });
            });
    }, [checkUser, msalInstance]);

    return <MsalProvider instance={msalInstance} children={children} />;
});

export default MsalConditionalProvider;
